import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { AddressModel, VatStatusEnum } from 'common/src/models';
import { CountryModel, CountryTransformFromIso2ToIso3Pipe } from 'common/src/modules/rnpl-common';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { PartnerVatCheckModel } from 'projects/workspace/src/app/partners/corporate/models/vat-check.model';
import { PartnersApiService } from 'projects/workspace/src/app/partners/corporate/services/partner-api.service';
import { AddressFormFactory } from 'projects/workspace/src/app/shared/forms/address-form.factory';
import { selectCountries } from 'projects/workspace/src/app/store/selectors/shared.selectors';
import { PartnerModel } from 'projects/workspace/src/app/partners/corporate/models/partner.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { convertAddressToStringHelper } from '../../../rnpl-common/helpers';
import { BranchOfficeModel } from 'projects/workspace/src/app/partners/corporate/models';
import { CommonModalsActionsEnum, DangerModalComponent } from '../../modals-common';

@Component({
  selector: 'rnpl-create-branch-office-modal',
  templateUrl: './create-branch-office-modal.component.html',
  styleUrls: ['./create-branch-office-modal.component.scss']
})
export class CreateBranchOfficeModalComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public displayEuSuffix: boolean = false;
  public canValidateVatNumber: boolean = true;

  readonly countries$: BehaviorSubject<CountryModel[]> = new BehaviorSubject<CountryModel[]>([]);
  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private readonly partnerApiService: PartnersApiService,
    private readonly store: Store<AppState>,
    private readonly toasterService: ToasterService,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    public dialogRef: MatDialogRef<CreateBranchOfficeModalComponent>,
    private countryTransformFromIso2ToIso3Pipe: CountryTransformFromIso2ToIso3Pipe,
    @Inject(MAT_DIALOG_DATA) public data: {
      partner: PartnerModel;
      partnerId: number;
      branchOffice: BranchOfficeModel;
      disabled: boolean;
    }) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.initForm();
    this.bindRegistrationCountryChanges();
    this.bindVatFromPartnerControlChanges();
    this.selectCountries();
  }

  initForm(): void {
    this.form = this.fb.group({
      registrationCountry: [{value: null, disabled: !this.data.branchOffice}, [Validators.required]],
      vatNumber: [{value: null, disabled: !this.data.branchOffice || this.data.branchOffice.vatFromPartner}, {validators: [Validators.required], updateOn: 'blur'}],
      vatFromPartner: [false],
      name: [null, [Validators.required]],
      legalAddress: AddressFormFactory.getForm(
        {} as AddressModel,
        false,
        true,
        ['country_iso3', 'street', 'zip_code', 'city']
      ),
      email: [null],
      phone: [null],
    });

    if (this.data.branchOffice) {
      this.form.patchValue(this.data.branchOffice);
      this.registrationCountry.patchValue(this.data.branchOffice.registrationCountry.codeISO3, {emitEvent: false});
      this.vatFromPartner.patchValue(!this.data.branchOffice.vatFromPartner);
    }

    if (this.data.disabled) {
      this.form.disable();
    }
  }

  private selectCountries(): void {
    this.store.select(selectCountries)
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries: CountryModel[]) => {
        this.countries$.next(countries);
      });
  }

  public getFormValue(): BranchOfficeModel {
    return {
      ...this.form.getRawValue(),
      vatFromPartner: !this.form.getRawValue().vatFromPartner
    };
  }

  submit() {
    if (this.submitRequest$.getValue()) { return; }
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    this.submitRequest$.next(true);

    this.partnerApiService.createPartnerBranchOffices(this.data.partnerId || this.data.partner.id, this.getFormValue())
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe((branchOffice: BranchOfficeModel) => this.dialogRef.close(branchOffice.id));
  }

  public submitUpdate(): void {
    if (this.submitRequest$.getValue()) { return; }
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    this.submitRequest$.next(true);

    this.partnerApiService.updateBranchOffice(
      this.data.branchOffice.id,
      {...this.data.branchOffice, ...this.getFormValue()}
    )
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.dialogRef.close(true));
  }

  public deleteBranchOffice(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'BRANCH_OFFICE.DELETE_BRANCH_TTL',
        message: 'BRANCH_OFFICE.DELETE_BRANCH_MSG',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2'
      },
    });

    dialog.afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.partnerApiService.deleteBranchOffice(this.data.branchOffice.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.dialogRef.close(true));
      }
    });
  }

  public bindVatFromPartnerControlChanges() {
    this.vatFromPartner.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        if (!value) {
          this.vatNumber.reset(null);
          this.vatNumber.disable();
          this.registrationCountry.disable({emitEvent: false});
        } else {
          this.registrationCountry.enable({emitEvent: false});
          this.vatNumber.enable();
        }
      });
  }

  public bindRegistrationCountryChanges(): void {
    this.registrationCountry.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        const currentCountry = this.countries.find(country => country.code_iso3 === value);
        this.legalAddressCountry.setValue(value);

        if (value && currentCountry.isVatEnabled) {
          // if (this.vatFromPartner.value) {
            this.vatNumber.enable({emitEvent: false});
          // }
          // this.vatFromPartner.enable({emitEvent: false});
        } else {
          this.vatNumber.disable({emitEvent: false});
          // this.vatFromPartner.disable({emitEvent: false});
        }

        if (!!currentCountry) {
          this.displayEuSuffix = !currentCountry.isEu;
        }
        if (currentCountry.isEu) {
          this.vatNumber.setValidators(Validators.required);
        } else {
          this.vatNumber.clearValidators();
        }
        this.vatNumber.updateValueAndValidity();
      });
  }

  processVatValidation(vatData: PartnerVatCheckModel): void {
    if (vatData.vatStatus === VatStatusEnum.NA) {
      this.canValidateVatNumber = false;
      return;
    } else {
      this.canValidateVatNumber = true;
    }

    if (!vatData.valid) { return; }
    if (this.data.branchOffice) { return; }

    if (vatData.name !== '---') {
      this.name.setValue(vatData.name);
    }

    if (vatData.address !== '---') {
      const arrAddress = vatData.address.split(' ');
      let isAddressAndZipFound = false;
      const parsedAddress = {
        street: [],
        houseAndZip: [],
        houseBlockApartments: [],
        city: [],
        zip: '',
      };

      for (const i in arrAddress) {
        if (!isAddressAndZipFound && !arrAddress[i].includes('/')) { // while not found zip all items is parts of street name
          parsedAddress.street.push(arrAddress[i]);
        } else if (!isAddressAndZipFound && arrAddress[i].includes('/')) {
          // (house, block, apartments) separates with (zip code) by '↵' sign
          parsedAddress.houseAndZip = arrAddress[i].split(/(\r\n|\n|\r)/gm);
          // house/block?(optional)/apartments - separates by '/' sign
          parsedAddress.houseBlockApartments = parsedAddress.houseAndZip[0].split('/');
          parsedAddress.zip = parsedAddress.houseAndZip[2];
          isAddressAndZipFound = true;
        } else { // when zip found all items is parts of city name
          parsedAddress.city.push(arrAddress[i]);
        }
      }

      this.setControlValue(this.legalAddress, 'street', parsedAddress.street.join(' '));
      this.setControlValue(this.legalAddress, 'city', parsedAddress.city.join(' '));
      this.setControlValue(this.legalAddress, 'zip_code', parsedAddress.zip);
      this.setControlValue(this.legalAddress, 'house_number', parsedAddress.houseBlockApartments[0]);

      if (parsedAddress.houseBlockApartments.length === 2) { // without block number
        this.setControlValue(this.legalAddress, 'apartments', parsedAddress.houseBlockApartments[1]);
      } else { // with block number
        this.setControlValue(this.legalAddress, 'block', parsedAddress.houseBlockApartments[1]);
        this.setControlValue(this.legalAddress, 'apartments', parsedAddress.houseBlockApartments[2]);
      }
    }
  }

  setControlValue(form: AbstractControl, control: string, val: any): void {
    if (!form.get(control).value) {
      form.get(control).setValue(val);
      form.get(control).markAsTouched();
    }
  }

  public get vatPrefix() {
    if (!this.registrationCountry.value) { return; }
    if (!this.countries.length) { return; }

    if (this.registrationCountry.value === 'che') { // exception for switzerland
      return 'CHE';
    }

    const selectedCountry = this.countries.find(country => country.code_iso3 === this.registrationCountry.value);
    return !selectedCountry.isEu ? 'EU' : selectedCountry.code_iso2.toUpperCase();
  }

  public get legalAddressAsText(): string {
    if (!this.form) { return ''; }
    return convertAddressToStringHelper(this.legalAddress.value, this.countries);
  }

  public get countries(): CountryModel[] {
    return this.countries$.getValue();
  }

  public get selectedCountry(): CountryModel {
    if (!this.registrationCountry.value) { return null; }
    return this.countries.find(country => country.code_iso3 === this.registrationCountry.value);
  }

  public addressChange(address: any) {
    let postalCode = null;
    let city = '';
    let street = '';
    let countryIso = '';
    let state = '';
    let house = null;

    address.address_components.map(component => {
      switch (component.types[0]) {
        case 'postal_code':
          postalCode = component.long_name;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'country':
          countryIso = this.countryTransformFromIso2ToIso3Pipe.transform(component.short_name).toLowerCase();
          break;
        case 'route':
          street = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.long_name;
          break;
        case 'street_number':
          house = component.long_name;
          break;
      }
    });

    if (!this.zip_code.value) { this.zip_code.setValue(postalCode); }
    if (!this.city.value) { this.city.setValue(city); }
    if (!this.state.value) { this.state.setValue(state); }
    if (!this.house_number.value) { this.house_number.setValue(house); }
    if (!this.name.value) { this.name.setValue(address.name); }
    if (!this.phone.value) { this.phone.setValue(address.international_phone_number); }
    if (!this.street.value) { this.street.setValue(street); }

    this.legalAddressCountry.setValue(countryIso);
    this.registrationCountry.setValue(countryIso);
  }

  private static removeAutocompleteElement(): void {
    document.querySelectorAll('.pac-container').forEach(el => el.remove());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    CreateBranchOfficeModalComponent.removeAutocompleteElement();
  }

  public get registrationCountry(): FormControl { return this.form.get('registrationCountry') as FormControl; }
  public get vatNumber(): FormControl { return this.form.get('vatNumber') as FormControl; }
  public get vatFromPartner(): FormControl { return this.form.get('vatFromPartner') as FormControl; }
  public get name(): FormControl { return this.form.get('name') as FormControl; }
  public get legalAddress(): FormGroup { return this.form.get('legalAddress') as FormGroup; }
  public get legalAddressCountry() { return this.form.get('legalAddress').get('country_iso3') as FormControl; }
  public get email(): FormControl { return this.form.get('email') as FormControl; }
  public get phone(): FormControl { return this.form.get('phone') as FormControl; }

  get city(): FormControl { return this.legalAddress.get('city') as FormControl; }
  get state(): FormControl { return this.legalAddress.get('state') as FormControl; }
  get street(): FormControl { return this.legalAddress.get('street') as FormControl; }
  get house_number(): FormControl { return this.legalAddress.get('house_number') as FormControl; }
  get zip_code(): FormControl { return this.legalAddress.get('zip_code') as FormControl; }

}
